/**
 * 四个必须留证的节点(M-04)。**单点定义在这里,三端引用**,
 * 与契约 components.schemas.ProofNode 的 enum 逐字一致,顺序也一致。
 *
 * ═══ 这四个取值为什么是「必须」而不是「建议」═══
 *
 * 照片是**监管端的兜底证据**。客户要用它证明「我们不是在做违法的事情」,
 * 所以 M-04 把它定成硬闸门:缺一张就不许推进状态。
 * 选填等于没有 —— 一个允许跳过的证据链,在真的需要它的那一天必然是空的,
 * 而那一天不会有人提前通知。
 *
 * ═══ 四个节点各自证的是什么 ═══
 *
 *   picked_up       取货:货真的从客户手里交出来了(以及交出来时长什么样)
 *   took_off        起飞:这一趟真的飞了,不是「说飞了」
 *   delivered       送达:货真的到了收货人手里
 *   drone_returned  无人机空机返回:飞机回来了、而且是空的 ——
 *                   这一张是四张里最容易被当成多余的,也是唯一能证明
 *                   「货没有被留在机上带回来」的东西
 *
 * 起降场监控接不到时由飞手补拍(BLK-05 的降级已由客户认可),
 * 所以四张全部是「飞手手机拍的」,不依赖任何外部画面源。
 *
 * ═══ 顺序有意义 ═══
 *
 * 它与状态推进一一对应(apps/api/src/modules/order/order.state.ts 的
 * PROOFS_REQUIRED_BY_STATUS)。改这里的顺序不会报错,但会让那张表读起来
 * 和履约次序不一致 —— 而那张表是「推进到某个状态之前要有哪几张照片」的唯一答案。
 */
export const PROOF_NODE = ['picked_up', 'took_off', 'delivered', 'drone_returned'] as const

export type ProofNode = (typeof PROOF_NODE)[number]

export function isProofNode(x: unknown): x is ProofNode {
  return typeof x === 'string' && (PROOF_NODE as readonly string[]).includes(x)
}

/** 给人看的节点名。错误话术里要用它 —— 飞手读到的不该是 drone_returned(M-18) */
export const PROOF_NODE_LABEL: Record<ProofNode, string> = {
  picked_up: '取货',
  took_off: '起飞',
  delivered: '送达',
  drone_returned: '无人机空机返回',
}

/** 「缺了这几张」的人话。缺哪个节点必须说得清(M-04、M-18) */
export function describeProofNodes(nodes: readonly ProofNode[]): string {
  return nodes.map((n) => `${PROOF_NODE_LABEL[n]}(${n})`).join('、')
}

// ───────────────────────────────────────────────────────
// 「走到哪一步了」—— 飞手端两页共用的一份推导(S-39)
//
// 两个页面都要回答同一个问题:「这一单下一个该拍的节点是哪一个」——
// 「我抢到的订单」卡片上那句「下一步:{节点}拍照留证」、那条四节点轨道填到第几格,
// 以及作业页卡头那个「N / 4」,说的都是同一件事。
//
// 两页各写一份的代价很具体:轨道填 2 格而卡片写「下一步:起飞」的那一天,
// 两处都渲染得出来、都不报错,只是互相矛盾 —— 而这个矛盾只有盯着两个页面同时看
// 的人才发现得了。所以推导只在这里一次,两页引用。
//
// **口径是「位置」不是「已留证几张」**:第一张还没拍的节点**之前(含它)**都算
// 「已经走到这里」。单据刚接下、零张凭证时是 1(站在「取货」这一格),
// 四张齐全时是 4 —— 画板 v2 · M7 的轨道(待取货的单填第一格)与 M8 的「2 / 4」
// 数的都是它。数「已留证几张」是另一件事,那个数在 M15 的「N / 4 已留证」上。
// ───────────────────────────────────────────────────────

/**
 * 下一个该拍的节点 = **第一张还没留证的**(四张齐全时 null)。
 *
 * @param proofNode 节点清单(共享包 PROOF_NODE;从参数进来,守卫能拿真实清单喂它)
 * @param doneNodes 这一单已经留过证的节点(服务端 proofs[].node)
 */
export function nextProofNode(proofNode: readonly ProofNode[], doneNodes: readonly string[]): ProofNode | null {
  for (let i = 0; i < proofNode.length; i++) {
    const node = proofNode[i]
    if (node === undefined) continue
    if (doneNodes.indexOf(node) < 0) return node
  }
  return null
}

/**
 * 走到第几格(**位置口径**,见上面那一节):第一张还没拍的节点是第几格就返回几;
 * 四张齐全时返回节点总数。返回值永远在 1..proofNode.length。
 */
export function proofStepIndex(proofNode: readonly ProofNode[], doneNodes: readonly string[]): number {
  for (let i = 0; i < proofNode.length; i++) {
    const node = proofNode[i]
    if (node === undefined) continue
    if (doneNodes.indexOf(node) < 0) return i + 1
  }
  return proofNode.length
}
